import { forwardRef, useState, type ChangeEvent, type ReactNode, type SelectHTMLAttributes } from 'react';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import { Caret } from './Caret';
import { FieldFrame } from './FieldFrame';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean | undefined;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label: string;
  labelHidden?: boolean | undefined;
  /** Options as data; alternatively pass `<option>` children. */
  options?: ReadonlyArray<SelectOption> | undefined;
  /** Empty first entry shown while nothing is selected ("Bitte wählen"). */
  placeholder?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  labelEnd?: ReactNode | undefined;
  leading?: ReactNode | undefined;
  size?: 'default' | 'compact' | undefined;
  tone?: 'card' | 'sunken' | undefined;
  /** Renders the value like a read-only field (`--surface-sunken`) and disables the control. */
  readOnly?: boolean | undefined;
}

/** Native select styled like the text field, with the `▾` indicator of the screens. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    labelHidden,
    options,
    placeholder,
    hint,
    error,
    invalid,
    required,
    optional,
    labelEnd,
    leading,
    size,
    tone,
    readOnly,
    className,
    id,
    disabled,
    children,
    value,
    defaultValue,
    onChange,
    ...rest
  },
  ref,
) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  const [uncontrolledEmpty, setUncontrolledEmpty] = useState(
    defaultValue === undefined || defaultValue === '',
  );
  const empty = value === undefined ? placeholder !== undefined && uncontrolledEmpty : value === '';

  function handleChange(event: ChangeEvent<HTMLSelectElement>) {
    setUncontrolledEmpty(event.target.value === '');
    onChange?.(event);
  }
  return (
    <FieldFrame
      htmlFor={ids.inputId}
      label={label}
      labelHidden={labelHidden}
      required={required}
      optional={optional}
      labelEnd={labelEnd}
      hint={hint}
      error={error}
      hintId={ids.hintId}
      errorId={ids.errorId}
      invalid={invalid}
      readOnly={readOnly}
      disabled={disabled}
      leading={leading}
      trailing={<Caret size={11} />}
      size={size}
      tone={tone}
      className={className}
    >
      <select
        ref={ref}
        id={ids.inputId}
        className={clsx(styles.select, empty && styles.placeholder)}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={ids.describedBy}
        aria-required={required || undefined}
        aria-readonly={readOnly || undefined}
        disabled={disabled || readOnly}
        value={value}
        defaultValue={defaultValue ?? (placeholder !== undefined && value === undefined ? '' : undefined)}
        onChange={handleChange}
        {...rest}
      >
        {placeholder !== undefined ? (
          <option value="" disabled={required}>
            {placeholder}
          </option>
        ) : null}
        {options
          ? options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))
          : children}
      </select>
    </FieldFrame>
  );
});
